import dayjs from 'dayjs'
import { makeAutoObservable, runInAction } from 'mobx'

import { notificationService } from '@/Core'
import { crudResourceRegistry } from '@/Core/Services/CrudResourceRegistry'
import { createFormContext } from '@/Core/UI/Form/Providers'
import { serverMessage, serverStatus } from '@/Ship/Helpers/serverError'

import { classifyVerificationViolations, violationsOf } from './classifyVerificationViolations'
import { CRM_VERIFICATION_TEXTS as T } from './CrmVerification'
import { RunCrmVerificationTransitionTask } from './RunCrmVerificationTransitionTask'
import { verificationOwnerKey, verificationRequirements } from './VerificationRequirementsStore'

import type {
    CrmVerificationOwnerConfig,
    CrmVerificationPermissions,
    CrmVerificationTransition,
    CrmViolation,
} from './CrmVerification'
import type { CrmRejectBody, CrmVerifyBody } from './RunCrmVerificationTransitionTask'
import type { FormStore } from '@/Core/UI/Form/Stores/FormStore'

/**
 * Те, що кнопки верифікації беруть із ядрового `crudContext` картки власника.
 * Описано вузько: ядровий стор написаний на `any`, і повний контракт тут не
 * потрібен.
 */
export interface VerificationCrudContextLike {
    resource?: { key?: string }
    formContext?: {
        values?: Record<string, unknown>
        isEditable?: boolean
        setInitialValues?: (values: Record<string, unknown>) => void
        setStorage?: (key: string, value: unknown) => void
    }
    actions?: {
        edit?: (item: Record<string, unknown>) => unknown
        reloadData?: () => unknown
    }
    listCrudContext?: { listContext?: { actions?: { load?: (force?: boolean) => unknown } } }
}

/**
 * Статуси, після яких картка вже не та, що на екрані: 400 — перехід зі
 * стану, якого вже немає (хтось змінив запис раніше), 403 — права змінилися,
 * 404 — запис видалено. Модалку тоді лишати безглуздо, а картку — перечитати,
 * щоб кнопки показали справжній стан.
 */
const STALE_CARD_STATUSES = new Set([400, 403, 404])

const isFilled = (value: unknown): boolean => {
    if (value === null || value === undefined) return false
    if (typeof value === 'string') return value.trim() !== ''
    if (Array.isArray(value)) return value.length > 0

    return true
}

/** Uuid юрособи з того, що тримає select: вкладений об'єкт, IRI або сам uuid. */
const uuidOf = (value: unknown): string | null => {
    if (value !== null && typeof value === 'object') {
        const record = value as Record<string, unknown>

        if (typeof record['uuid'] === 'string' && record['uuid'] !== '') return record['uuid']

        return uuidOf(record['@id'])
    }

    if (typeof value === 'string' && value !== '') return value.split('/').filter(Boolean).pop() ?? null

    return null
}

/** Дата договору — без часу й поясу: `YYYY-MM-DD`. */
const dateOf = (value: unknown): string | null => {
    if (value === null || value === undefined || value === '') return null

    const date = dayjs(value as string)

    return date.isValid() ? date.format('YYYY-MM-DD') : null
}

/**
 * Переходи верифікації однієї картки власника (`SPEC-008` §7.2,
 * `SPEC-009` §8.6): модалки, запити, обробка відповіді.
 *
 *  - **«Відправити на верифікацію»**, **«З чорного списку»** — одразу запит;
 *  - **«Підтвердити верифікацію»** — модалка з експедитором (передзаповнений
 *    з картки, КЛ-19), номером і датою договору (КЛ-18);
 *  - **«Відхилити верифікацію»** — модалка з причиною (КЛ-20);
 *  - **«В чорний список»** — діалог-підтвердження: перехід знімає дані
 *    верифікації (КЛ-21).
 *
 * Після успіху картка бере нову версію запису з відповіді (статус, `_permissions`,
 * `isVerified` → вкладка «Друковані форми»), а список під шухлядою
 * перечитується — разом із ним і цифри на плашках статусів.
 *
 * На 422 вимог картка переходить у редагування з підсвіченими полями
 * (`VerificationRequirementsStore`, КЛ-17); 422 полів модалки лягає під поля
 * модалки.
 */
export class VerificationStore {
    pending: CrmVerificationTransition | null = null

    verifyOpen = false
    verifyForm: FormStore | null = null
    verifyError: string | null = null

    rejectOpen = false
    rejectComment = ''
    rejectError: string | null = null

    blacklistOpen = false

    constructor(
        readonly config: CrmVerificationOwnerConfig,
        private crudContext: VerificationCrudContextLike
    ) {
        makeAutoObservable<VerificationStore, 'crudContext'>(this, { config: false, crudContext: false })
    }

    /** Ядро кличе `onButtons` на кожен рендер — тримаємо свіжий контекст. */
    bind(crudContext: VerificationCrudContextLike): void {
        this.crudContext = crudContext
    }

    get permissions(): CrmVerificationPermissions {
        const permissions = this.crudContext.formContext?.values?.['_permissions']

        return permissions !== null && typeof permissions === 'object' ? (permissions as CrmVerificationPermissions) : {}
    }

    /** Клік по пункту меню кнопок. */
    start(transition: CrmVerificationTransition): void {
        if (this.pending !== null) return

        switch (transition) {
            case 'verify':
                this.openVerify()
                return
            case 'reject_verification':
                this.rejectComment = ''
                this.rejectError = null
                this.rejectOpen = true
                return
            case 'blacklist':
                this.blacklistOpen = true
                return
            default:
                void this.run(transition)
        }
    }

    // ---- «Підтвердити верифікацію» -----------------------------------------

    private openVerify(): void {
        const forwarder = this.crudContext.formContext?.values?.['forwarder']

        this.verifyError = null
        this.verifyForm = createFormContext({
            fields: {
                contractForwarder: {
                    label: this.config.texts.forwarderLabel,
                    type: 'select',
                    resource: this.config.forwarderResource,
                    isRequired: true,
                },
                contractNumber: {
                    label: T.contractNumber,
                    isRequired: true,
                    inputProps: { maxLength: 150 },
                },
                contractDate: {
                    label: T.contractDate,
                    type: 'date',
                    isRequired: true,
                },
            },
            // Експедитор — з картки, якщо заданий (КЛ-19); поле картки
            // підтвердження не змінює
            initialValues: isFilled(forwarder) ? { contractForwarder: forwarder } : {},
            onSubmit: ({ values }: { values: Record<string, unknown> }) => this.submitVerify(values),
        }) as FormStore
        this.verifyOpen = true
    }

    closeVerify(): void {
        this.verifyOpen = false
        this.verifyForm = null
        this.verifyError = null
    }

    /** «Зберегти» модалки: ядрова форма спершу перевіряє обов'язкові поля. */
    submitVerifyForm(): void {
        void this.verifyForm?.submit()
    }

    private async submitVerify(values: Record<string, unknown>): Promise<void> {
        const contractForwarder = uuidOf(values['contractForwarder'])
        const contractDate = dateOf(values['contractDate'])
        const contractNumber = typeof values['contractNumber'] === 'string' ? values['contractNumber'].trim() : ''

        // Порожнє ядрова форма вже не пропустила — тут лише те, що не
        // розібралося (дата не дата, експедитор без uuid)
        if (contractForwarder === null || contractDate === null || contractNumber === '') {
            this.verifyError = T.contactAdmin
            return
        }

        const body: CrmVerifyBody = { contractForwarder, contractNumber, contractDate }

        await this.run('verify', body)
    }

    // ---- «Відхилити верифікацію» -------------------------------------------

    setRejectComment(value: string): void {
        this.rejectComment = value
    }

    get canReject(): boolean {
        return this.rejectComment.trim() !== ''
    }

    closeReject(): void {
        this.rejectOpen = false
        this.rejectError = null
    }

    submitReject(): void {
        if (!this.canReject) return

        const body: CrmRejectBody = { comment: this.rejectComment.trim() }

        void this.run('reject_verification', body)
    }

    // ---- «В чорний список» -------------------------------------------------

    closeBlacklist(): void {
        this.blacklistOpen = false
    }

    confirmBlacklist(): void {
        void this.run('blacklist')
    }

    // ---- Запит і відповідь -------------------------------------------------

    private async run(transition: CrmVerificationTransition, body?: CrmVerifyBody | CrmRejectBody): Promise<void> {
        const uuid = this.crudContext.formContext?.values?.['uuid']

        if (typeof uuid !== 'string' || uuid === '' || this.pending !== null) return

        this.pending = transition

        try {
            const card = await RunCrmVerificationTransitionTask(this.config.restUrl, uuid, transition, body ?? {})

            runInAction(() => {
                this.pending = null
                this.closeAll()
            })

            verificationRequirements.forget(verificationOwnerKey(this.config.restUrl, uuid))
            notificationService.showSuccess(T.successTitle, this.config.texts.success[transition])

            await this.applyCard(card)
            this.reloadList()
        } catch (error: unknown) {
            runInAction(() => {
                this.pending = null
            })

            this.handleError(transition, uuid, error)
        }
    }

    private handleError(transition: CrmVerificationTransition, uuid: string, error: unknown): void {
        const status = serverStatus(error)
        const violations = status === 422 ? violationsOf((error as { response?: unknown }).response) : []

        if (violations.length > 0) {
            const { requirements, body } = classifyVerificationViolations(violations, {
                hasBody: transition === 'verify',
            })

            if (requirements.length > 0) {
                this.showRequirements(uuid, requirements)
                return
            }

            if (transition === 'verify' && this.verifyForm) {
                // Помилки полів модалки — під полями модалки
                this.verifyForm.setErrors(Object.fromEntries(body.map(({ field, message }) => [field, message])))
                return
            }

            if (transition === 'reject_verification' && this.rejectOpen) {
                this.rejectError = body.map(({ message }) => message).join(' ') || T.contactAdmin
                return
            }
        }

        const message = serverMessage(error, T.contactAdmin)
        const isStale = status !== null && STALE_CARD_STATUSES.has(status)

        // Модалка лишається відкритою, лише якщо її ще можна виправити й
        // відправити знову (обрив зв'язку, 500); застарілу картку — перечитуємо
        if (!isStale && this.verifyOpen) {
            this.verifyError = message
            return
        }

        if (!isStale && this.rejectOpen) {
            this.rejectError = message
            return
        }

        this.closeAll()
        notificationService.showError(T.errorTitle, message)

        if (isStale) void this.reloadCard()
    }

    /**
     * 422 вимог (`SPEC-008` §5.4.2, КЛ-17): модалку закриваємо, бо виправляти
     * треба картку, а не її. Хто може змінювати запис — опиняється в ньому в
     * режимі редагування з помилками під полями й переліком незаповненого.
     * Хто не може — бачить перелік у повідомленні: відкривати форму, у якій
     * нічого не збережеш, безглуздо.
     */
    private showRequirements(uuid: string, requirements: CrmViolation[]): void {
        this.closeAll()

        const ownerKey = verificationOwnerKey(this.config.restUrl, uuid)
        const values = this.crudContext.formContext?.values ?? {}
        const edit = this.crudContext.actions?.edit
        const canEdit = this.permissions.update === true && typeof edit === 'function'

        verificationRequirements.set(ownerKey, requirements)

        if (canEdit) {
            notificationService.showError(T.errorTitle, T.fillRequired)
            edit(values)
            return
        }

        const labels = requirements.map(({ field, message }) => this.config.fieldLabels[field] ?? message)

        notificationService.showError(T.errorTitle, `${T.fillRequired} Не заповнено: ${labels.join(', ')}.`)
    }

    private closeAll(): void {
        this.verifyOpen = false
        this.verifyForm = null
        this.verifyError = null
        this.rejectOpen = false
        this.rejectError = null
        this.blacklistOpen = false
    }

    /**
     * Нова версія картки — з відповіді переходу, якщо в ній є `_permissions`;
     * інакше картку перечитуємо (`?permissions=true`), щоб кнопки не лишилися
     * від старого стану.
     */
    private async applyCard(card: Record<string, unknown> | null): Promise<void> {
        const form = this.crudContext.formContext
        const permissions = card?.['_permissions']

        if (card && permissions !== null && typeof permissions === 'object' && form?.setInitialValues) {
            form.setInitialValues(card)
            form.setStorage?.('responseData', { ...card })
            return
        }

        await this.reloadCard()
    }

    private async reloadCard(): Promise<void> {
        try {
            await this.crudContext.actions?.reloadData?.()
        } catch {
            // Запис могли видалити — тоді лишається те, що вже на екрані, і
            // повідомлення сервера, показане вище
        }
    }

    /** Список під шухлядою: статус-плашки перераховуються після кожного його завантаження. */
    private reloadList(): void {
        const load = this.crudContext.listCrudContext?.listContext?.actions?.load

        if (typeof load === 'function') {
            load(true)
            return
        }

        const key = this.crudContext.resource?.key

        if (typeof key === 'string' && key !== '') crudResourceRegistry.reloadListIfExists(key)
    }
}

const stores = new WeakMap<object, VerificationStore>()

/**
 * Стор переходів картки — один на ядровий `crudContext`. Кнопки (`onButtons`)
 * і модалки (`VerificationDialogs`) беруть той самий екземпляр: ядровий
 * `ButtonGroupStore` копіює конфіг кнопок, тож стан у самих кнопках жити не
 * може.
 */
export const verificationStoreFor = (
    crudContext: VerificationCrudContextLike,
    config: CrmVerificationOwnerConfig
): VerificationStore => {
    let store = stores.get(crudContext)

    if (!store) {
        store = new VerificationStore(config, crudContext)
        stores.set(crudContext, store)
    } else {
        store.bind(crudContext)
    }

    return store
}
